import type { SetEnvironmentDefaultInput } from './flags';
import type { FeatureFlagDefault } from '../schemas/flag';

/**
 * Build a `PUT /flags/:key/default/:environment` body that changes one field
 * and preserves the rest.
 *
 * Same reasoning as `defaultWriteFrom` — this PUT **replaces** the row, so an
 * omitted field is a cleared field, not an unchanged one — with one addition:
 * an environment partition can be genuinely empty. `current` is `undefined`
 * when `environmentDefaults[environment]` was absent on the flag the screen
 * loaded, meaning there is no row to round-trip *from* at all. That is the
 * first-write case the API's `expectedVersion` nullability exists for (see
 * `SetEnvironmentDefaultInput`): this function sends `expectedVersion: null`
 * rather than treating "no current row" as "current row with default field
 * values" and guessing a version.
 *
 * Deliberately does **not** seed `enabled`/`archived`/`rolloutPercentage` from
 * the platform default when `current` is absent. A first write to an
 * environment partition is a new row with its own identity, not an implicit
 * copy of the global one — the caller decides what a fresh override should
 * say by passing it in `change`.
 */
/**
 * What a fresh environment override should say when a column with no row of
 * its own is first given one, via `EnvironmentMatrix`'s "Override" button —
 * every field copied from the platform default so the new row resolves
 * identically to what the column was inheriting a moment ago.
 *
 * `environmentDefaultWriteFrom` deliberately does not seed from the platform
 * default itself (see its own docs above) — that stays a UI decision the
 * caller makes. This is that decision, factored out so the "Override" button
 * cannot drop a field again the way it once did: seeding only `enabled` left
 * `rolloutPercentage` and `archived` unset, which the replace-the-row PUT
 * resolves as "no gate" and "not archived" — silently promoting a
 * 25%-gated platform default to 100% for that environment on one click, with
 * no confirm dialog to catch it.
 */
export function overrideSeedFrom(platformDefault: FeatureFlagDefault): {
    enabled: boolean;
    rolloutPercentage?: number;
    archived: boolean;
} {
    return {
        enabled: platformDefault.enabled,
        archived: platformDefault.archived,
        ...(platformDefault.rolloutPercentage !== undefined
            ? { rolloutPercentage: platformDefault.rolloutPercentage }
            : {}),
    };
}

export function environmentDefaultWriteFrom(
    current: FeatureFlagDefault | undefined,
    change: { enabled?: boolean; rolloutPercentage?: number; archived?: boolean },
    reason?: string,
): SetEnvironmentDefaultInput {
    const rolloutPercentage = change.rolloutPercentage ?? current?.rolloutPercentage;

    return {
        enabled: change.enabled ?? current?.enabled ?? false,
        archived: change.archived ?? current?.archived ?? false,
        // The version the screen was rendered from, or `null` when it showed no
        // row for this environment — never a guessed number.
        expectedVersion: current?.version ?? null,
        ...(rolloutPercentage !== undefined ? { rolloutPercentage } : {}),
        ...(reason !== undefined ? { reason } : {}),
    };
}
